import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom-v5-compat';

import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';

import { ForecastingDrawerContent } from './forecasting-drawer-content';
import { ForecastingDrawerFooter } from './forecasting-drawer-footer';
import { useForecastingData } from './hooks/use-forecasting-data';
import { usePurchaseOrderIntegration } from './hooks/use-purchase-order-integration';
import messages from './messages';
import { useExportCSV } from './utils/csv-export/use-export-csv';
import { trackAbandon, trackCsvExport } from './utils/reorder-assist-tracking';
import { PurchaseOrderProvider } from '../../pages/products-page/purchase-orders/purchase-order-context';
import { PurchaseOrderDrawer } from '../../pages/products-page/purchase-orders/purchase-order-drawer/purchase-order-drawer';

/**
 * Container component that has access to forecasting data context, handles purchase orders and tracking
 * @param {object} props - Component props
 * @param {() => void} props.onClose - Function to close the drawer
 * @returns {React.ReactElement} The rendered drawer content with purchase order functionality and tracking
 */
export const ForecastingDrawerContainer = ({ onClose }) => {
  const { inventoryWarehouseId } = useParams();
  const { data, quantityValues, rawEntities } = useForecastingData();
  const exportCSV = useExportCSV();

  const { isPurchaseOrderDrawerOpen, initializedPurchaseOrder, handleCreatePurchaseOrderClick, closePurchaseOrderDrawer } =
    usePurchaseOrderIntegration(data, quantityValues, rawEntities);

  const handlePurchaseOrderClose = useCallback(() => {
    closePurchaseOrderDrawer();
    onClose(); // Close the forecasting drawer when purchase order drawer closes
  }, [closePurchaseOrderDrawer, onClose]);

  const handleClose = useCallback(() => {
    // Track abandon event
    if (data?.length > 0) {
      trackAbandon(data, quantityValues, inventoryWarehouseId);
    }
    onClose();
  }, [data, quantityValues, inventoryWarehouseId, onClose]);

  const handleExportCSV = useCallback(() => {
    // Track CSV export event
    if (data?.length > 0) {
      trackCsvExport(data, quantityValues, inventoryWarehouseId);
    }

    // Perform the actual export
    exportCSV(data, rawEntities);
  }, [data, quantityValues, rawEntities, exportCSV, inventoryWarehouseId]);

  return (
    <>
      <DrawerHeader
        closeButtonAriaLabel={messages.closeDrawerAriaLabel}
        onClose={handleClose}
        title={<FormattedMessage {...messages.drawerTitle} />}
      />
      <ForecastingDrawerContent />
      <ForecastingDrawerFooter exportCSV={handleExportCSV} onCreatePurchaseOrderClick={handleCreatePurchaseOrderClick} />
      {isPurchaseOrderDrawerOpen && initializedPurchaseOrder ? (
        <PurchaseOrderProvider initializedPurchaseOrder={initializedPurchaseOrder} onClose={handlePurchaseOrderClose}>
          <PurchaseOrderDrawer />
        </PurchaseOrderProvider>
      ) : null}
    </>
  );
};
